import { memo, useCallback, useMemo, useState } from "react";
import { useTranslate } from "helpers/common";
import { NotificationSearchFormValues } from "services/api/notifications/types";
import {
  initialNotificationSearchForm,
  ENTITY_TYPE_LABELS,
  ACTIVE_FILTER_OPTIONS,
  SCOPE_FILTER_OPTIONS,
  CHANNEL_FILTER_OPTIONS,
  CUSTOM_FILTER_OPTIONS,
} from "services/api/notifications/consts";
import { useHandleTextFieldChange } from "services/forms/hooks";
import { Dropdown } from "ui/components/Dropdown/Dropdown";
import { DropdownOption } from "ui/components/Dropdown/Dropdown/types";
import { TextField } from "ui/components/TextField/TextField";
import { Button } from "ui/components/Button/Button";
import { NotificationSearchFilterProps } from "./types";

const NotificationSearchFilters: React.FC<NotificationSearchFilterProps> = (props) => {
  const { onSearchChange, onAddNew } = props;

  const { t } = useTranslate();

  const [formValues, setFormValues] = useState<NotificationSearchFormValues>(
    initialNotificationSearchForm
  );

  const handleResetFormClick = useCallback(() => {
    setFormValues(initialNotificationSearchForm);
    onSearchChange(initialNotificationSearchForm);
  }, [onSearchChange]);

  const handleDropdownChange = useCallback(
    (_e: any, name: string, item: DropdownOption) => {
      const updatedFormValues = { ...formValues, [name]: item.value || null };
      setFormValues(updatedFormValues);
      onSearchChange(updatedFormValues);
    },
    [formValues, onSearchChange]
  );

  // TextField sam debounce-a unos, pa se pretraga ne okida na svako slovo.
  const handleSearchFieldChange = useHandleTextFieldChange(
    (updatedFormValues: NotificationSearchFormValues) => {
      setFormValues(updatedFormValues);
      onSearchChange(updatedFormValues);
    },
    formValues
  );

  const entityTypeOptions: DropdownOption[] = useMemo(
    () =>
      Object.entries(ENTITY_TYPE_LABELS).map(([value, text]) => ({
        value,
        text,
      })),
    []
  );

  return (
    <div className=" ">
      <div className="flex justify-between items-center gap-3">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="min-w-[220px]">
            <TextField
              type="text"
              name="search"
              placeholder={t("admin.notification.filter.search.placeholder")}
              value={formValues.search}
              onChange={handleSearchFieldChange}
              width="medium"
              cssClassName="bg-white"
            />
          </div>

          <div className="min-w-[200px]">
            <Dropdown
              name="entityType"
              placeholder={t("admin.notification.filter.entitytype.placeholder")}
              options={entityTypeOptions}
              onChange={handleDropdownChange}
              value={formValues.entityType ?? ""}
              width="medium"
              cssClassName="bg-white"
            />
          </div>

          <div className="min-w-[180px]">
            <Dropdown
              name="scope"
              placeholder={t("admin.notification.filter.scope.placeholder")}
              options={SCOPE_FILTER_OPTIONS}
              onChange={handleDropdownChange}
              value={formValues.scope ?? ""}
              width="small"
              cssClassName="bg-white"
            />
          </div>

          <div className="min-w-[160px]">
            <Dropdown
              name="channel"
              placeholder={t("admin.notification.filter.channel.placeholder")}
              options={CHANNEL_FILTER_OPTIONS}
              onChange={handleDropdownChange}
              value={formValues.channel ?? ""}
              width="small"
              cssClassName="bg-white"
            />
          </div>

          <div className="min-w-[180px]">
            <Dropdown
              name="isCustom"
              placeholder={t("admin.notification.filter.kind.placeholder")}
              options={CUSTOM_FILTER_OPTIONS}
              onChange={handleDropdownChange}
              value={formValues.isCustom ?? ""}
              width="small"
              cssClassName="bg-white"
            />
          </div>

          <div className="min-w-[160px]">
            <Dropdown
              name="isActive"
              placeholder={t("admin.notification.filter.status.placeholder")}
              options={ACTIVE_FILTER_OPTIONS}
              onChange={handleDropdownChange}
              value={formValues.isActive ?? ""}
              width="small"
              cssClassName="bg-white"
            />
          </div>

          <div className="flex items-center">
            <span
              onClick={handleResetFormClick}
              className="text-xs font-bold cursor-pointer text-gray-700"
            >
              RESET
            </span>
          </div>
        </div>

        <div className="py-3 flex gap-2 justify-end ">
          <Button
            variant="primary"
            startIcon="/media/icons/plus.svg"
            onClick={onAddNew}
          >
            {t("admin.notification.create")}
          </Button>
        </div>
      </div>
    </div>

  );
};

export default memo(NotificationSearchFilters);
